import { connect } from 'react-redux';
import { useState, useEffect, useRef } from 'react';

import { login, setLoading, setAlert } from '../actions/defaultActions';
import { isObject, isArray, getServerApiResponse } from '../Util';
import { REQUEST_LOGIN_CODE, CHECK_LOGIN_CODE, SiteboardApiValidator } from '../actions/types';
import { ERROR_MESSAGES } from '../errorMessages';

import PopupWindow from './PopupWindow';
import PushButton from './PushButton';

import Logo from '../SiteBoard_Logo';

const LoginDialogue = ({ _isTouchDevice, login, setLoading, setAlert, closeDialogue }) => {
  const EMAIL_INPUT_PAGE = 'EMAIL_INPUT_PAGE';
  const LOGIN_CODE_PAGE = 'LOGIN_CODE_PAGE';
  const LOGIN_LINK_PAGE = 'LOGIN_LINK_PAGE';

  const emailRef = useRef(null);
  const loginCodeRef = useRef(null);
  const [inputEmail, setInputEmail] = useState('');
  const [inputLoginCode, setInputLoginCode] = useState('');
  const [currentPage, setCurrentPage] = useState(EMAIL_INPUT_PAGE);


  // Helper keys to switch screens for testing
  useEffect(() => {
    if(process.env.REACT_APP_LIVE_MODE === 'true') return;

    const onKeyUp = (event) => {
      if(event.key === 'ArrowUp') { 
        setCurrentPage(EMAIL_INPUT_PAGE);
        setInputEmail('');
      } else if(event.key === 'ArrowDown') { 
        setCurrentPage(LOGIN_CODE_PAGE);
        setInputEmail('keydown@gmail.com');
      } else if(event.key === 'ArrowRight') { 
        setCurrentPage(LOGIN_LINK_PAGE);
        setInputEmail('keyright@gmail.com');
      }
    };

    window.addEventListener('keyup', onKeyUp);
    return () => { window.removeEventListener('keyup', onKeyUp); }; // remove listener when component unloads
  }, []);

  const submitEmail = () => {
    let validInput = null;
    try { 
      validInput = SiteboardApiValidator[REQUEST_LOGIN_CODE].validateInput({
        action: REQUEST_LOGIN_CODE,
        email: inputEmail,
      });
    } 
    catch(error) { 
      setAlert(error.message); 
      return;
    }

    const onApiSuccess = (data) => {
      setLoading('');
      if(data.action === 'loginCodeEmailed') {
        setCurrentPage(LOGIN_CODE_PAGE);
      } else if(data.action === 'loginLinkEmailed') {
        setCurrentPage(LOGIN_LINK_PAGE);
      } else {
        setAlert(ERROR_MESSAGES.LOGIN);
        return;
      }
    };
    const onApiError = (errorMessage) => {
      setLoading('');
      setAlert(typeof errorMessage === 'string' ? errorMessage : ERROR_MESSAGES.LOGIN);
    };
    setLoading('Sending Login Code');
    getServerApiResponse(validInput, onApiSuccess, onApiError);
  };
  const submitLoginCode = () => {
    let validInput = null;
    try { 
      validInput = SiteboardApiValidator[CHECK_LOGIN_CODE].validateInput({
        action: CHECK_LOGIN_CODE,
        email: inputEmail,
        loginCode: inputLoginCode,
      });
    } 
    catch(error) { 
      setAlert(error.message); 
      return;
    }

    const onApiSuccess = (data) => {
      setLoading('');
      try {
        // TODO: check valid output respose here with validator
        if (!isObject(data.user) || typeof data.sessionKey !== 'string') throw new Error();
        login(data.user, data.sessionKey); 
      } catch(error) {
        setAlert(ERROR_MESSAGES.LOGIN_CODE);
      }
    };
    const onApiError = (errorMessage) => {
      setLoading('');
      setAlert(typeof errorMessage === 'string' ? errorMessage : ERROR_MESSAGES.LOGIN_CODE);
    };
    setLoading('Checking Login Code');
    getServerApiResponse(validInput, onApiSuccess, onApiError);
  };

  // focus input when component is shown
  useEffect(() => { 
    // Don't auto focus for touch devices because it changes the screen layout too much when the virtual keyboard appears
    if(_isTouchDevice === true) return;

    if(currentPage === LOGIN_CODE_PAGE) {
      if(loginCodeRef.current) {
        loginCodeRef.current.select();
      }
    } else {
      if(emailRef.current) {
        emailRef.current.select();
      }
    }
  }, [currentPage, _isTouchDevice]);

  const emailScreen = () => (
    <div className='flex flex-col gap-4'>
      {/* email input */}
      <div className='flex flex-col gap-y-[5px] w-[300px] max-w-full mx-auto'>
        <div>
          <label className='block text-sm font-medium mb-1'>Email</label>
          <input 
            ref={emailRef}
            value={inputEmail}
            className='w-full text-white rounded-md px-3 py-1.5 bg-[#444247] hover:bg-[#656565] focus:bg-[#656565]' 
            type='email' 
            onChange={(event) => { setInputEmail(event.target.value); }}
            onKeyDown={(event) => { if(event.key === 'Enter') { submitEmail(); } }}
          />
        </div>
      </div>
      <PushButton 
        textColor='white'
        bgColor='#471cff'
        hoverBgColor="#603aff"
        onClick={() => { submitEmail(); }}
      >
        Log in
      </PushButton>
    </div>
  );

  const loginCodeScreen = () => (
    <>
      {/* login code input */}
      <div className='w-[300px] max-w-full flex flex-col gap-y-4 mx-auto'>
        <div className='flex flex-col gap-0'>
          <div>We've sent a login code to:</div>
          <div className='font-bold'>{inputEmail}</div>
        </div>
        <div className='font-light opacity-50 mx-auto'>Didn&apos;t receive the code? Check your spam folder or wait up to 2 minutes for delivery.</div>
        <div className='flex flex-col gap-2'>
          <div className='flex flex-col gap-1'>
            <label className='block text-sm font-medium'>Login Code</label>
            <input 
              ref={loginCodeRef}
              value={inputLoginCode}
              className='w-full text-white rounded-md px-3 py-1.5 bg-[#444247] hover:bg-[#656565] focus:bg-[#656565]' 
              type='text' 
              placeholder="Enter your code"
              onChange={(event) => { setInputLoginCode(event.target.value); }}
              onKeyDown={(event) => { if(event.key === 'Enter') { submitLoginCode(); } }}
            />
          </div>
          {/* submit login code button */}
          <PushButton 
            textColor='white'
            bgColor='#471cff'
            hoverBgColor="#603aff"
            onClick={() => { submitLoginCode(); }}
          >
            Log in
          </PushButton>
        </div>

        {/* Resend Code */}
        <div className="flex flex-col items-center gap-2 text-center">
          <p className="text-sm text-gray-500">
            Didn't receive the code?
          </p>

          <button
            type="button"
            className="
              group inline-flex items-center gap-2
              text-sm font-semibold text-[#b5a7ff]
              transition-colors duration-200
              hover:text-white
              focus-visible:outline focus-visible:outline-2
              focus-visible:outline-offset-4
              focus-visible:outline-[#b5a7ff]
            "
            onClick={() => {
              submitEmail();
              setInputLoginCode("");
            }}
          >
            <svg
              className="
                h-4 w-4
                transition-transform duration-500
                group-hover:rotate-180
              "
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M20 11a8 8 0 1 0-2.5 6" />
              <path d="M20 4v7h-7" />
            </svg>

            Resend verification code
          </button>
        </div>

      </div>
    </>
  );

  const loginLinkScreen = () => (
    <>
      <div className='w-[300px] max-w-full flex flex-col gap-y-4 mx-auto'>
        <div>We've sent an email to <b>{inputEmail}</b> with a secure link to log in to your account. Please check your inbox and follow the link to log in.</div>
        <div className='font-light opacity-50 mx-auto'>Didn&apos;t receive the email? Check your spam folder or wait up to 2 minutes for delivery.</div>
      </div>
    </>
  );

  const popupHeaderJSX = () => {
    return (
      <div className='flex justify-center items-center gap-1.5'>
        <Logo size='1.5em' fillColor='#a59e9e' />
        <p className={`text-[1.8em] font-semibold text-[#a59e9e]`}>{process.env.REACT_APP_WEBSITE_NAME}</p>
      </div>
    );
  };

  return (
    <PopupWindow 
      title={popupHeaderJSX}
      closeDialogue={closeDialogue} 
      bgStyle='bg-[#333]'
      onBackButton={currentPage !== EMAIL_INPUT_PAGE ? () => { setCurrentPage(EMAIL_INPUT_PAGE); } : null }
    >
      <div className='text-[1.2em] text-white'>
        {currentPage === EMAIL_INPUT_PAGE && emailScreen()}
        {currentPage === LOGIN_CODE_PAGE && loginCodeScreen()}
        {currentPage === LOGIN_LINK_PAGE && loginLinkScreen()}
      </div>
    </PopupWindow>
 );
};

const mapStateToProps = state => { return { 
  _isTouchDevice: state.localEphemeral.isTouchDevice,
}};
export default connect(mapStateToProps, { login, setLoading, setAlert })(LoginDialogue);
